import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { experimentsService } from '@wix/cashier-common/dist/src/utils/experimentsService';
import React from 'react';
import { UIMFieldMeta } from '../../../utils/mandatoryFieldsMeta';
import { DeviceType } from '../../../types/DeviceType';
import { Experiment } from '../../../types/Experiment';
import { MFieldType } from '../../../types/MFieldType';
import { MFieldName } from '../../../types/MandatoryFields';
import { Theme } from '../../../types/Theme';
import { DH } from '../../../utils/DataHook';
import { MFieldState } from '../types';
import { BankDropdown } from './BankDropdown';
import { CountryDropdown } from './CountryDropdown';
import { StateDropdown } from './StateDropdown';
import { TextField } from './TextField';
import { BaseFieldProps, FieldState } from './types';
import { getFieldValidatorFn, logValidationError } from './utils';

interface FieldProps {
  theme?: Theme;
  deviceType?: DeviceType;
  locale: string;
  shouldApplySiteStyles?: boolean;
  host: string;
  countryCode?: string;
  values?: any[];
  paymentMethod?: PaymentMethod; // for BI

  data?: UIMFieldMeta;
  formFieldData: MFieldState;

  inputRef?(ref: HTMLInputElement): void;
  onChange(fieldData: { value: string; isValid: boolean }): void;
}

export class Field extends React.Component<FieldProps> {
  static defaultProps: Partial<FieldProps> = {
    deviceType: 'desktop',
    theme: Theme.default,
    countryCode: '',
  };

  handleFieldChange = ({ value, isValid }: FieldState) => {
    this.props.onChange({ value, isValid });
  };

  handleFieldChangeDone = ({ value, name, isValid }: FieldState) => {
    this.handleFieldChange({ value, name, isValid });

    if (value && !isValid) {
      experimentsService.ready().then(() => {
        if (
          experimentsService.isExperimentEnabled(
            Experiment.LogCheckoutValidationErrors
          )
        ) {
          logValidationError({
            fieldName: name,
            fieldValue: value,
            errorMessageKey: this.getErrorMessage(),
            paymentMethod: this.props.paymentMethod,
          });
        }
      });
    }
  };

  get commonFieldProps(): BaseFieldProps {
    const {
      data,
      shouldApplySiteStyles,
      deviceType,
      theme,
      locale,
      formFieldData,
    } = this.props;
    const { name, type, required } = data;
    const placeholder = this.getPlaceholder();

    return {
      name: name as MFieldName,
      type,
      required,
      label: this.getLabel(),
      locale,
      errorMessage: this.getErrorMessage(),
      ...(placeholder && { placeholder }),
      shouldApplySiteStyles,
      deviceType,
      theme,
      isValid: formFieldData.isValid,
      touched: formFieldData.touched,
      value: formFieldData.value || '',

      onChange: this.handleFieldChange,
      validate: getFieldValidatorFn(data.type),
    };
  }

  getLabel = () => {
    const { data, countryCode } = this.props;
    const { label, labelForCountry } = data;
    return (
      (countryCode &&
        labelForCountry &&
        labelForCountry[countryCode.toLowerCase()]) ||
      label
    );
  };

  getErrorMessage = () => {
    const { data, countryCode } = this.props;
    const { errorMessage, errorMessageForCountry } = data;
    return (
      (countryCode &&
        errorMessageForCountry &&
        errorMessageForCountry[countryCode.toLowerCase()]) ||
      errorMessage
    );
  };

  getPlaceholder = () => {
    const { data, countryCode } = this.props;
    const { placeholder, placeholderForCountry } = data;
    return (
      (countryCode &&
        placeholderForCountry &&
        placeholderForCountry[countryCode.toLowerCase()]) ||
      placeholder
    );
  };

  renderFieldByType() {
    const { data, host, countryCode, formFieldData } = this.props;

    switch (data.type) {
      case MFieldType.CountryCode: {
        return (
          <CountryDropdown
            {...this.commonFieldProps}
            host={host}
            value={formFieldData.value || countryCode}
            onChange={this.handleFieldChangeDone}
          />
        );
      }
      case MFieldType.State: {
        return (
          <StateDropdown
            {...this.commonFieldProps}
            host={host}
            countryCode={countryCode}
          />
        );
      }
      case MFieldType.IssuerBank:
        return (
          <BankDropdown
            {...this.commonFieldProps}
            bankList={this.props.values}
            onChange={this.handleFieldChangeDone}
          />
        );
      case MFieldType.Email:
        return (
          <TextField
            {...this.commonFieldProps}
            value={formFieldData.value || ''}
            onBlur={this.handleFieldChangeDone}
          />
        );
      case MFieldType.PhoneTel:
        return (
          <TextField
            {...this.commonFieldProps}
            inputRef={this.props.inputRef}
            value={formFieldData.value || ''}
            onBlur={this.handleFieldChangeDone}
            numbersOnly
          />
        );
      default: {
        return (
          <TextField
            {...this.commonFieldProps}
            inputRef={this.props.inputRef}
            value={formFieldData.value || ''}
            onBlur={this.handleFieldChangeDone}
          />
        );
      }
    }
  }

  render() {
    return (
      <div data-hook={DH.PaymentDetails.FieldContainer}>
        {this.renderFieldByType()}
      </div>
    );
  }
}
